import {
  Radio as MantineRadio,
  RadioCardProps as MantineRadioCardProps,
  RadioGroupContext,
} from '@mantine/core';
import { forwardRef, useContext } from 'react';

import { ThemeIcon, ThemeIconColor } from '../ThemeIcon';
import styles from './RadioCard.module.css';
import { RadioGroupReadOnlyContext } from './RadioGroup';
import { RadioIndicator } from './RadioIndicator';

export type RadioCardProps = Omit<
  MantineRadioCardProps,
  'style' | 'styles' | 'classNames'
> & {
  /** Icon element to display on the left side of the card */
  icon?: React.ReactNode;
  /** Color variant for the ThemeIcon wrapper */
  iconColor?: ThemeIconColor;
  /** Primary label text */
  label?: string;
  /** Secondary description text below the label */
  description?: string;
  /** When true, hides selection indicator and disables pointer events (display-only mode). Implied inside a read-only RadioGroup. */
  isReadOnly?: boolean;
  /** Size of the card. `lg` is the default for backward compatibility */
  size?: 'md' | 'lg';
  /** Content to display on the right side (e.g. pricing info). In withoutBorder layout, rendered after the separator line. */
  rightContent?: React.ReactNode;
  /** When true, renders the borderless layout with RadioIndicator on the left */
  isBorderless?: boolean;
};

export const RadioCard = forwardRef<HTMLButtonElement, RadioCardProps>(
  (
    {
      icon,
      iconColor = 'blue',
      label,
      description,
      disabled,
      isReadOnly: isReadOnlyProp = false,
      size = 'lg',
      children,
      rightContent,
      isBorderless,
      ...props
    },
    ref
  ) => {
    const isGroupReadOnly = useContext(RadioGroupReadOnlyContext);
    const groupValue = useContext(RadioGroupContext)?.value;
    const isChecked =
      typeof props.checked === 'boolean'
        ? props.checked
        : groupValue === props.value;

    if (isGroupReadOnly && !isChecked) return null;

    const isReadOnly = isReadOnlyProp || isGroupReadOnly;
    const themeIconSize = size === 'md' ? '24' : '32';

    return (
      <MantineRadio.Card
        ref={ref}
        unstyled
        classNames={{
          card: styles.card,
        }}
        data-has-description={description ? true : undefined}
        data-readonly={isReadOnly ? true : undefined}
        data-input-size={size}
        data-without-border={isBorderless ? true : undefined}
        disabled={disabled}
        {...props}
      >
        {children ?? (
          <div className={styles.content}>
            {isBorderless && !isReadOnly && (
              <RadioIndicator disabled={disabled} />
            )}

            <div className={styles.left}>
              {icon && (
                <ThemeIcon size={themeIconSize} color={iconColor}>
                  {icon}
                </ThemeIcon>
              )}
              <div className={styles.labelGroup}>
                {label && <span className={styles.label}>{label}</span>}
                {description && (
                  <span className={styles.description}>{description}</span>
                )}
              </div>
            </div>

            {/* The dashed separator only ever connects the label to something
                on the right, so it needs rightContent and no description (the
                taller two-line block leaves the dash floating above the text). */}
            {isBorderless && !isReadOnly && !description && rightContent && (
              <div className={styles.separator} />
            )}

            {/* Right content (optional in both layouts; hidden in readonly per Figma) */}
            {rightContent && !isReadOnly && (
              <div className={styles.rightContent}>{rightContent}</div>
            )}

            {/* With-border: indicator goes RIGHT */}
            {!isBorderless && !isReadOnly && (
              <RadioIndicator disabled={disabled} />
            )}
          </div>
        )}
      </MantineRadio.Card>
    );
  }
);

RadioCard.displayName = 'RadioCard';
